import React from 'react';
import { Route } from 'react-router-dom';
import PrivateRoute from './PrivateRoute';
import PermissionRoute from './PermissionRoute';
import TechnicianDashboard from '../pages/technicians/TechnicianDashboard';
import ManagerDashboard from '../pages/manager/ManagerDashboard';
import StaffPage from '../pages/staffs/StaffDashboard';
import AccountsDashboard from './../pages/accountpage/AccountsDashboard';
import ManagerMyApprovals from './../pages/manager/MyApprovals';
import ManagerTeamUsersPage from '../pages/manager/ManagerTeamUsersPage';
import ManagerTeamListPage from '../pages/manager/ManagerTeamListPage';
import ManagerPreventiveMaintenancePage from '../pages/manager/ManagerPreventiveMaintenancePage';
import ManagerComplaintsPage from '../pages/manager/ManagerComplaintsPage';
import MyApprovals from './../pages/accountpage/MyApprovals';
import Invoice from './../pages/accountpage/AccountInvoicePage';
import Layout from '../MainLayout/index';
import CreateTaskPage from '../pages/tasks/CreateTaskPage';
import TaskDetailPage from '../pages/tasks/TaskDetailPage';
import RoleReviewFormsPage from '../pages/forms/RoleReviewFormsPage';
import RoleFormAssignmentsPage from '../pages/forms/RoleFormAssignmentsPage';
import FormsAssignedByMePage from '../pages/forms/FormsAssignedByMePage';
import RoleFormFillPage from '../pages/forms/RoleFormFillPage';
import FormSubmissionViewPage from '../pages/forms/FormSubmissionViewPage';
import MyFormResultsPage from '../pages/forms/MyFormResultsPage';
import FormReviewQueuePage from '../pages/forms/FormReviewQueuePage';
import FormExecutionReportsPage from '../pages/forms/FormExecutionReportsPage';
import FormsWorkspacePage from '../pages/forms/FormsWorkspacePage';
import FormTypeManagementPage from '../pages/admins/FormTypeManagementPage';
import FormBuilderPage from '../pages/forms/FormBuilderPage';
import MyWorkPage from '../pages/tasks/MyWorkPage';
import ComplaintsListPage from '../pages/tasks/ComplaintsListPage';
import MyTicketsListPage from '../pages/tasks/MyTicketsListPage';
import TaskApprovalPolicyManagementPage from '../pages/tasks/TaskApprovalPolicyManagementPage';
import MyTaskApprovalQueuePage from '../pages/tasks/MyTaskApprovalQueuePage';
import MyPerformanceDashboardPage from '../pages/dashboard/MyPerformanceDashboardPage';
import ReportsPage from '../pages/reports/ReportsPage';
import DeveloperDashboard from '../pages/developer/DeveloperDashboard';
import DataAnalystDashboard from '../pages/dataAnalyst/DataAnalystDashboard';
import MyKraKpiPerformancePage from '../pages/dataAnalyst/performance/MyKraKpiPerformancePage';
import MyKraWorkPage from '../pages/dataAnalyst/kraWork/MyKraWorkPage';
import KpiEvidencePage from '../pages/developer/KpiEvidencePage';
import KpiEvidenceReviewPage from '../pages/admins/KpiEvidenceReviewPage';
import ProjectManagementPage from '../pages/admins/ProjectManagementPage';
import ProjectDetailPage from '../pages/admins/ProjectDetailPage';

// Every non-admin role shares the exact same set of pages below its own
// `/<role>/` prefix — task creation, form assignment/fill/results/review,
// work queues, etc. are never role-specific PAGES, only permission-gated
// ones (via <Can>/usePermissions() inside each page, or backend
// requirePermission()). So the route TABLE itself must not hardcode a
// per-role entry — that would mean granting e.g. Developer or a future
// "Data Analyst" role tasks:create through Access Management -> Role
// Permissions still wouldn't get them a route to reach the page at all.
// Adding a new role needs ZERO changes here: whatever `user.role` string
// comes back from auth automatically gets this exact shared page set at
// `/<that-role>/...`, with access to each page still fully governed by
// permissions, never by this route table.
const SHARED_ROUTES = [
  // 'tasks/create' ("Raise Complaint" for Staff -- CreateTaskPage's own
  // `isStaff` check forces complaint mode regardless of this route; "Create
  // Ticket" for Developer/Data-Analyst -- the FULL, non-complaint form,
  // IT_SUPPORT hidden from its task-type dropdown; existing Create Task for
  // Technician/Manager, unchanged) requires tasks:create -- NOT tasks:view.
  // A role with no tasks:create grant cannot reach this by URL even though
  // the page itself never calls GET /tasks.
  {
    path: 'tasks/create',
    element: (
      <PermissionRoute module="tasks" action="create">
        <CreateTaskPage />
      </PermissionRoute>
    ),
  },
  // 'tasks/raise-complaint' -- the dedicated Raise Complaint route (mode=
  // "complaint"): IT_SUPPORT auto-selected, complaint-oriented labels,
  // Project/Department/Assigned-To/Staff-User fields hidden. Separate from
  // 'tasks/create' ("Create Ticket") above so Developer/Data-Analyst never
  // land on the same UI mode for two semantically different actions. Any
  // role with tasks:create can reach this -- it is additive, not a
  // Developer/Data-Analyst-only path.
  {
    path: 'tasks/raise-complaint',
    element: (
      <PermissionRoute module="tasks" action="create">
        <CreateTaskPage mode="complaint" />
      </PermissionRoute>
    ),
  },
  // Role* (never Admin*) wrappers -- see pages/forms/RoleFormAssignmentsPage.jsx
  // /RoleReviewFormsPage.jsx/RoleFormFillPage.jsx. Admin reaches the SAME
  // underlying *Content components through its own separate AdminFormAssignmentsPage
  // /AdminReviewFormsPage/AdminFormFillPage, registered only in routes/AppRoutes.js's
  // /admin block -- never through this shared table.
  //
  // Role-policy hardening: 'form-assignments' ("Assign Forms") and
  // 'my-assigned-forms' ("Review Forms") are wrapped in PermissionRoute so
  // that a role with no form_assignments:create / assigned_forms:view_own
  // grant (e.g. Staff) cannot reach these pages by typing the URL directly
  // -- sidebar visibility alone was never enforcement. This is
  // permission-driven, not role-name-driven: whichever roles a future DB
  // grant covers get access automatically, with zero route-table changes.
  {
    path: 'form-assignments',
    element: (
      <PermissionRoute module="form_assignments" action="create">
        <RoleFormAssignmentsPage />
      </PermissionRoute>
    ),
  },
  { path: 'forms-assigned-by-me', element: <FormsAssignedByMePage /> },
  {
    path: 'my-assigned-forms',
    element: (
      <PermissionRoute module="assigned_forms" action="view_own">
        <RoleReviewFormsPage />
      </PermissionRoute>
    ),
  },
  { path: 'my-assigned-forms/:assignmentUserId/fill/:submissionId', element: <RoleFormFillPage /> },
  { path: 'form-submissions/:submissionId', element: <FormSubmissionViewPage /> },
  { path: 'my-form-results', element: <MyFormResultsPage /> },
  { path: 'form-review-queue', element: <FormReviewQueuePage /> },
  { path: 'form-execution-reports', element: <FormExecutionReportsPage /> },
  // Dynamic Forms visibility audit, §7: the consolidated Forms Workspace
  // (Build / Assign & Track / Review & Reports, incl. the new generic
  // Responses tab) previously existed ONLY under /admin/forms -- every
  // other role was permanently stuck on the standalone pages above. Self-
  // gates internally per tab (FormsWorkspacePage.jsx), same posture as
  // 'forms-assigned-by-me' above -- no PermissionRoute wrapper needed here.
  { path: 'forms', element: <FormsWorkspacePage /> },
  // Dynamic Forms visibility audit: the Build tab's "Manage Form Types" /
  // "Create Form"/"Edit"/"New Version" actions now follow this SAME role
  // prefix (FormsBuildTab.jsx/FormBuilderPage.jsx) instead of a hardcoded
  // /admin/ path -- these two deep-link pages must exist here too, mounted
  // exactly the way /admin's own AppRoutes.js mounts them (no extra
  // PermissionRoute wrapper -- both pages self-gate per action via <Can>,
  // and the backend independently enforces form_types:*/forms:* on every
  // API call regardless).
  { path: 'form-types', element: <FormTypeManagementPage /> },
  { path: 'forms/:formId/builder/:versionId', element: <FormBuilderPage /> },
  { path: 'my-work', element: <MyWorkPage /> },
  // "Complaints" -- ONE common list page for every role. No PermissionRoute
  // wrapper: the page itself picks a safely-scoped endpoint at runtime --
  // Developer/Data-Analyst ALWAYS get GET /my-complaints (created_by = me
  // AND task_type = IT_SUPPORT, a role override inside the page itself);
  // every other role keeps the original behavior (GET /tasks when
  // can('tasks','view') is true -- Admin/Manager; GET /tasks/user/:id,
  // self-scoped, otherwise -- Staff/Accounts). Never fetches an unscoped
  // list and filters client-side. task_type (not assignment direction) is
  // the complaint/ticket discriminator -- see
  // services/taskAssignmentService.js#listMyComplaints.
  { path: 'complaints', element: <ComplaintsListPage /> },
  // Full Task Detail Page (replaces the old TaskViewModal popup) -- shared
  // by every role, permission-gated internally the same way the modal was
  // (Can moduleKey="tasks"/"task_assignments" per action). GET /tasks/:id
  // itself already enforces access server-side, same as before.
  { path: 'tasks/:taskId', element: <TaskDetailPage /> },
  // "My Tasks" is the caller's official assignment queue. Project/milestone
  // tasks are created centrally, so assigned_to (not created_by) defines
  // whether the work belongs to the logged-in user. MyWorkPage defaults to
  // the trusted GET /my-assigned-tasks endpoint and links every row to the
  // full TaskDetailPage.
  { path: 'my-tasks', element: <MyWorkPage assignedOnly /> },
  // Preserve the older created-by-me ticket list on an explicit compatibility
  // route instead of defining "My Tasks" by who created the row.
  { path: 'my-created-tasks', element: <MyTicketsListPage /> },
  { path: 'task-approval-policies', element: <TaskApprovalPolicyManagementPage /> },
  { path: 'my-task-approvals', element: <MyTaskApprovalQueuePage /> },
  { path: 'my-performance-dashboard', element: <MyPerformanceDashboardPage /> },
  { path: 'reports', element: <ReportsPage /> },
  // Phase 31: KRA/KPI Evidence & Evaluation workflow for FORM/MANUAL-sourced
  // KPIs -- a page wholly separate from the finalized, portal-only
  // Developer Home (routes/DynamicRoutes.jsx's own `home` entry above).
  // Permission-gated (never role-hardcoded), same pattern as
  // 'form-assignments'/'my-assigned-forms' above.
  {
    path: 'my-kpi-evidence',
    element: (
      <PermissionRoute module="kpi_evidence" action="view">
        <KpiEvidencePage />
      </PermissionRoute>
    ),
  },
  {
    path: 'kpi-evidence-review',
    element: (
      <PermissionRoute module="kpi_evidence" action="review">
        <KpiEvidenceReviewPage />
      </PermissionRoute>
    ),
  },
  // Project list + full Project Workspace (Overview/Process/Board/Timeline/
  // Milestones/Activity) -- the EXACT SAME components Admin already uses at
  // /admin/projects and /admin/projects/:projectId (routes/AppRoutes.js),
  // reused here rather than duplicated. GET /projects (and every workspace
  // tab's own GET) already applies the caller's resolved data_scope
  // server-side (ALL/PROJECT/UNIT/OWN/SELF per role_permissions) -- a
  // PROJECT-scoped grant (e.g. Developer, via project_members OWNER/
  // MANAGER rows) naturally returns only that user's own projects, with
  // zero client-side filtering added here. Wrapped in PermissionRoute the
  // same way 'form-assignments'/'my-assigned-forms' above are: a role with
  // no projects:view grant at all cannot reach this by typing the URL
  // directly. `can()`/`isAllowed` is scope-independent (see
  // PermissionContext.js/services/permissionService.js) -- PROJECT scope
  // resolves isAllowed=true exactly like ALL scope does, only the resolved
  // dataScope narrows which ROWS come back, so this does not redirect a
  // PROJECT-scoped Developer to /unauthorized.
  {
    path: 'projects',
    element: (
      <PermissionRoute module="projects" action="view">
        <ProjectManagementPage />
      </PermissionRoute>
    ),
  },
  {
    path: 'projects/:projectId',
    element: (
      <PermissionRoute module="projects" action="view">
        <ProjectDetailPage />
      </PermissionRoute>
    ),
  },
];

// Only the "home" landing dashboard genuinely differs per role (a real UI
// design choice, not an access-control one) — a role with no bespoke
// dashboard here (e.g. "developer", or any future role) falls back to the
// existing, neutral StaffDashboard rather than getting no home page at all.
const ROLE_HOME = {
  technician: { path: 'home', element: <TechnicianDashboard /> },
  manager: { path: 'home', element: <ManagerDashboard /> },
  staff: { path: 'home', element: <StaffPage /> },
  accounts: { path: 'home', element: <AccountsDashboard /> },
  developer: { path: 'home', element: <DeveloperDashboard /> },
  'data-analyst': { path: 'home', element: <DataAnalystDashboard /> },
};
const DEFAULT_HOME = { path: 'home', element: <StaffPage /> };

// A small number of pages genuinely only make sense for one specific
// existing role's own workflow (manager approvals, accounts invoicing) —
// these are additive on top of SHARED_ROUTES, not a replacement pattern.
// Team Users CRUD. The page is reachable only with users:view_team; the
// /api/manager/users endpoints re-check users:*_team and scope every query
// to the caller's own unit server-side. Buttons inside follow the caller's
// own create/update/delete_team grants.
const TEAM_USERS_ROUTE = {
  path: 'users',
  element: (
    <PermissionRoute module="users" action="view_team">
      <ManagerTeamUsersPage />
    </PermissionRoute>
  ),
};

// Team List: read-only own-unit directory (Manager only). Same
// users:view_team gate as Team Users; /api/manager/team-list re-checks it
// and scopes to the caller's own unit server-side.
const TEAM_LIST_ROUTE = {
  path: 'team-list',
  element: (
    <PermissionRoute module="users" action="view_team">
      <ManagerTeamListPage />
    </PermissionRoute>
  ),
};

// Preventive Maintenance (Manager only): own-unit monthly checks. Gated by
// preventive_maintenance:view; /api/manager/preventive-maintenance re-checks
// view/create/update and always uses the caller's own unit server-side.
// Technicians keep /technician/preventive-maintenance (AppRoutes.js).
const PREVENTIVE_MAINTENANCE_ROUTE = {
  path: 'preventive-maintenance',
  element: (
    <PermissionRoute module="preventive_maintenance" action="view">
      <ManagerPreventiveMaintenancePage />
    </PermissionRoute>
  ),
};

// Team Complaints (Manager only): replaces the shared Complaints page for
// this role with the Manager Home task table (ManagerComplaintsPage.jsx).
// Data comes from GET /tasks?complaintsOnly=1 -- own-unit IT Support only,
// scoped server-side by req.user.unit_id.
const MANAGER_COMPLAINTS_ROUTE = { path: 'complaints', element: <ManagerComplaintsPage /> };

const ROLE_EXTRA_ROUTES = {
  manager: [
    MANAGER_COMPLAINTS_ROUTE,
    { path: 'approvals', element: <ManagerMyApprovals /> },
    TEAM_USERS_ROUTE,
    TEAM_LIST_ROUTE,
    PREVENTIVE_MAINTENANCE_ROUTE,
  ],
  technician: [TEAM_USERS_ROUTE],
  // Phase 62: KRA/KPI "My Performance" -- Data Analyst only. The API behind
  // it is self-scoped to the session user; the route exists for no other role.
  // Phase 63: "My KRA Work" -- the operational records behind the KPIs (one
  // page, five KRA tabs). Path is 'kra-work' because 'my-work' is the shared
  // tasks page. Its API is analyst_work-permissioned and owner-scoped.
  'data-analyst': [
    { path: 'performance', element: <MyKraKpiPerformancePage /> },
    { path: 'kra-work', element: <MyKraWorkPage /> },
  ],
  accounts: [
    { path: 'approvals', element: <MyApprovals /> },
    { path: 'invoice', element: <Invoice /> },
  ],
};

const DynamicRoutes = ({ user }) => {
  if (!user) return null;

  // Explicit route ownership (not "whichever route the router happens to
  // match first"): Admin owns its own complete route tree in
  // routes/AppRoutes.js's `/admin` block (AdminFormAssignmentsPage,
  // AdminReviewFormsPage, etc.) and must never ALSO be generated here --
  // this table is the shared table for every OTHER (non-Admin) role.
  if (user.role === 'admin') return null;

  const role = user.role;
  const home = ROLE_HOME[role] || DEFAULT_HOME;
  // Role-specific extras are looked up by a normalized key so a role stored
  // as e.g. "Data Analyst" / "data_analyst" still gets the 'data-analyst'
  // pages. Paths and the PrivateRoute gate below keep the raw role string.
  const roleKey = String(role || '').trim().toLowerCase().replace(/[\s_]+/g, '-');
  const extraRoutes = ROLE_EXTRA_ROUTES[role] || ROLE_EXTRA_ROUTES[roleKey] || [];
  // A role-specific route replaces the shared route with the same path
  // (e.g. Manager -> Team Complaints) instead of registering it twice.
  const overridden = new Set(extraRoutes.map((route) => route.path));
  const routes = [home, ...extraRoutes, ...SHARED_ROUTES.filter((route) => !overridden.has(route.path))];

  return (
    <Route element={<Layout />}>
      {routes.map((route, index) => (
        <Route
          key={index}
          path={`/${role}/${route.path}`}
          element={
            <PrivateRoute roles={[role]}>
              {route.element}
            </PrivateRoute>
          }
        />
      ))}
    </Route>
  );
};

export default DynamicRoutes;
